/*
 * Copyright (c) 2024 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useState} from 'react';
import {makeStyles, Menu, MenuItem} from '@material-ui/core';
import {translate} from '@ion/core/src/locale/locale';
import {DropdownArrowIcon} from '@ion/dls/src/icons/DropdownArrowIcon';
import {dlsColor, dlsTypography} from '@ion/dls/src/theme/theme';
import {DLSTheme} from '@ion/dls/src/types/types';

export type JobActionSelectionMenuProps = {
  count: number;
  handleSelect: () => void;
  handleDeselect: () => void;
  disable: boolean;
};

const useStyles = makeStyles((theme: DLSTheme) => ({
  menu : {
    marginTop         : '48px',
    marginLeft        : '-16px',
    '& .MuiList-root' : {
      paddingBottom : '0px !important'
    }
  },
  menuItem : {
    ...dlsTypography('body2'),
    color     : dlsColor('grey2'),
    minWidth  : '250px',
    fontSize  : '14px',
    height    : '48px',
    padding   : '12px 16px !important',
    '&:hover' : {
      backgroundColor : dlsColor('paleBlue')
    }
  }
}));

/**
 * The JobActionSelectionMenu provides a Dropdown Menu which allows for selecting  
 * and / or deselecting all items in a column for the Job Conflict Resolution Chooser.
 * 
 * ```typescript
 *   import {JobActionSelectionMenu} from './JobActionSelectionMenu';
 * 
 *   <JobActionSelectionMenu count={count} 
 *     handleSelect={handleSelect} 
 *     handleDeselect={handleDeselect} 
 *     disable={disable} />
 * ```
 * 
 * @param count The number of items to select.
 * @param handleSelect The function to call when selecting all items.
 * @param handleDeselect The function to call when deselecting all items.
 * @param disable If true, the deselect option is disabled.
 */
export const JobActionSelectionMenu = ({
  count,
  handleSelect,
  handleDeselect,
  disable
}: JobActionSelectionMenuProps) => {
  const classes                 = useStyles({disable});
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open                    = Boolean(anchorEl);

  const handleClick = useCallback(function(event) {
    setAnchorEl(event.currentTarget);
  }, []);

  const handleClose = useCallback(function() {
    setAnchorEl(null);
  }, []);

  const onDeselect = useCallback(function() {
    handleClose();
    handleDeselect();
  }, [handleDeselect, handleClose]);

  const onSelection = useCallback(function() {
    handleClose();
    handleSelect();
  }, [handleSelect, handleClose]);

  return (
    <div>
      <div aria-controls={open ? 'basic-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
        onClick={handleClick}>
        <DropdownArrowIcon />
      </div>
      <Menu className={classes.menu}
        id={'job-action-selection-menu'}
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        MenuListProps={{'aria-labelledby': 'basic-button'}}>
        <MenuItem className={classes.menuItem}
          onClick={onSelection}>
          {translate('jobcreate:conflict.selectAll', {count})}
        </MenuItem>
        <MenuItem className={classes.menuItem}
          onClick={onDeselect}
          disabled={disable}>
          {translate('jobcreate:conflict.deselectAll')}
        </MenuItem>
      </Menu>
    </div>
  );
};

export default JobActionSelectionMenu;
